import { runFreshness } from './performanceRefresh';

/**
 * Raw JSON export of one Performance tab — a super-admin diagnostic.
 *
 * The tabs render cards built from the runs. When a tab is empty or a number
 * looks wrong, the question is "what did the server actually return for this
 * account": which runs exist, what analysisKind they carry, what `result`
 * holds, what `missing` says, which dismissals are suppressing cards. This
 * writes those responses AS RECEIVED, scoped to the tab:
 *
 *   Diagnostics / Budget & pacing / Optimization
 *       the /runs response with `runs` narrowed to the tab's analysis kind —
 *       the same rows the tab draws its cards from — plus the /dismissals
 *       response every tab is filtered through.
 *   Summary
 *       every run (its health read folds all three in) plus the portfolio
 *       fetches the view is drawn from: campaign grid, series, deltas and, when
 *       a range is picked, the windowed roll-up.
 *
 * `dataRefreshed` and the filename carry WHEN the exported analyses ran, not
 * just when the file was saved: an export of week-old runs downloaded today
 * must not read as today's data.
 *
 * Pure. Takes the hook results the page already holds, so the file is what the
 * page rendered from — not a second request that could disagree with it.
 */

/** Tab key → the focused analysis behind it. Summary has none. */
export const TAB_ANALYSIS_KIND = {
  diagnostics: 'diagnostic',
  pacing: 'pacing',
  recommendations: 'performance',
};

/** Where each block of the file came from, so a reader can re-issue the call. */
export const PERFORMANCE_ENDPOINTS = {
  runs: '/api/v2/performance/runs',
  dismissals: '/api/v2/performance/dismissals',
  grid: '/api/v2/campaign-grid',
  series: '/api/v2/performance/series',
  deltas: '/api/v2/performance/campaign-deltas',
  windowed: '/api/v2/performance/windowed-metrics',
};

/**
 * @param {object} args
 * @param {{key:string,label:string}} args.tab   the active TABS entry
 * @param {{data?:object,error?:string}} args.runs        usePerformanceRuns result
 * @param {{data?:object,error?:string}} args.dismissals  useDismissals result
 * @param {object|null} [args.portfolio]   usePortfolio's `raw` (Summary only)
 * @param {string|null} [args.accountId]
 * @param {string|null} [args.campaignId]  the drill-down campaign, if any
 * @param {Date} [args.now]
 */
export function buildPerformanceTabExport({
  tab, runs, dismissals, portfolio = null, accountId = null, campaignId = null, now = new Date(),
}) {
  const kind = TAB_ANALYSIS_KIND[tab.key] || null;
  const runsData = runs?.data || null;
  // The envelope (campaign, campaigns, missing, …) is kept whole — `missing` is
  // exactly what explains an empty tab — and only `runs` is narrowed.
  const scopedRuns = runsData
    ? {
      ...runsData,
      runs: kind
        ? (runsData.runs || []).filter((r) => r.analysisKind === kind)
        : (runsData.runs || []),
    }
    : null;

  const out = {
    tab: tab.key,
    tabLabel: tab.label,
    analysisKind: kind,
    exportedAt: now.toISOString(),
    dataRefreshed: dataRefreshed(scopedRuns?.runs),
    accountId,
    campaignId: campaignId || null,
    endpoints: { runs: PERFORMANCE_ENDPOINTS.runs, dismissals: PERFORMANCE_ENDPOINTS.dismissals },
    // A tab showing an error has no data to export; the error IS the finding.
    errors: { runs: runs?.error || null, dismissals: dismissals?.error || null },
    runs: scopedRuns,
    dismissals: dismissals?.data || null,
  };

  if (!kind) {
    out.endpoints = {
      ...out.endpoints,
      grid: PERFORMANCE_ENDPOINTS.grid,
      series: PERFORMANCE_ENDPOINTS.series,
      deltas: PERFORMANCE_ENDPOINTS.deltas,
      windowed: PERFORMANCE_ENDPOINTS.windowed,
    };
    // Null until PortfolioTab has handed its responses up (see its onRawData).
    out.portfolio = portfolio || null;
  }
  return out;
}

/**
 * When the exported runs completed: newest, oldest, how many, and how many ran
 * more than a day before the newest. Null when the export holds no completed run.
 */
export function dataRefreshed(runs) {
  const f = runFreshness(runs);
  if (!f) return null;
  return {
    newestCompletedAt: new Date(f.newest).toISOString(),
    oldestCompletedAt: new Date(f.oldest).toISOString(),
    runs: f.count,
    olderThanNewestByADay: f.older,
  };
}

/**
 * e.g. performance-budget-pacing-data-2026-09-01-exported-2026-09-06.json —
 * the data date first, since that is what the file is. Without completed runs
 * there is no data date: performance-budget-pacing-exported-2026-09-06.json.
 */
export function performanceExportFilename(tab, now = new Date(), refreshed = null) {
  const slug = String(tab.label || tab.key)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const data = refreshed?.newestCompletedAt ? `-data-${refreshed.newestCompletedAt.slice(0, 10)}` : '';
  return `performance-${slug}${data}-exported-${now.toISOString().slice(0, 10)}.json`;
}
